import * as React from 'react';
import { FC, useCallback, useState } from 'react';

import { Parser } from 'html-to-react';

import {
    Alert,
    BodyText1,
    BodyText2,
    FlexRow,
    Modal,
    ModalBody,
    ModalButton,
    ModalFooter,
    ModalHeader,
} from '../';
import { parseApiError } from '../../api/request';
import _ from '../../locale';
import { styled } from '../../theme';
import { SurveyResource, SurveyResponse } from '../../types';
import formatRichText from '../RichTextEditor/formatRichText';
import SurveyPreviewBanner from './SurveyPreviewBanner';
import SurveyQuestion from './SurveyQuestion';

const htmlParser = Parser();

const StyledModal = styled(Modal)({
    display: 'flex !important',
    alignItems: 'center',
    flexDirection: 'column',
    maxWidth: 540,
});

const StyledAlert = styled(Alert)({
    width: '100%',
});

const StyledHeader = styled(ModalHeader)({
    marginTop: 10,
    width: '100%',
    paddingRight: 20,
    paddingLeft: 20,
});

const StyledFooter = styled(ModalFooter)({
    width: '100%',
    justifyContent: 'space-between',
});

const StyledModalBody = styled(ModalBody)({
    padding: '20px 32px 40px',
    width: '100%',
    overflowWrap: 'anywhere',
});

const SurveyDescription = styled(FlexRow)({
    marginBottom: 24,
});

const SurveyProgress = styled(BodyText1)(({ theme }) => ({
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeight.heavy,
    fontSize: '1.6rem',
    color: theme.color.primary1,
    textTransform: 'uppercase',
}));

type Props = {
    survey: SurveyResource;
    previewMode?: boolean;
    onSubmit?: (surveyId: number, payload?: SurveyResponse[]) => Promise<any>;
    onHide: () => void;
    onPostpone?: () => void;
    onPublish?: () => void;
    className?: string;
};

const SurveyPopup: FC<Props> = ({
    survey,
    previewMode = false,
    onSubmit,
    onHide,
    onPostpone,
    onPublish,
    className,
}) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [answers, setAnswers] = useState({});
    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const questions = survey?.questions || [];
    const questionsCount = questions.length;
    const currentQuestion = questions[currentStep];
    const answer = answers[currentQuestion.id] || {};
    const isFirstStep = currentStep === 0;
    const isLastStep = currentStep === questionsCount - 1;

    const handleAnswerChange = useCallback(
        (value) => {
            setAnswers((prev) => ({ ...prev, [currentQuestion.id]: value }));
        },
        [currentQuestion.id, setAnswers]
    );

    const isCurrentStepValid = React.useMemo(() => {
        if (!answer) return false;

        switch (currentQuestion.type) {
            case 'single':
                return typeof answer.selected === 'number';
            case 'multi':
                return Array.isArray(answer.selected) && answer.selected.length > 0;
            case 'text':
                return !!answer.comment?.trim();
            default:
                return false;
        }
    }, [currentQuestion, answer]);

    const handleBack = useCallback(
        () => setCurrentStep((prev) => Math.max(prev - 1, 0)),
        [setCurrentStep]
    );

    const handleNext = useCallback(
        () => setCurrentStep((prev) => Math.min(prev + 1, questionsCount - 1)),
        [setCurrentStep, questions]
    );

    const handleDismiss = useCallback(async () => {
        if (previewMode) {
            onHide();
            return;
        }

        await onSubmit?.(survey.id)
            .then(() => onHide())
            .catch((err) => setError(parseApiError(err)));
    }, [onHide, onSubmit]);

    const handleShowLater = useCallback(() => {
        !previewMode && onPostpone?.();
        onHide();
    }, [onHide, onPostpone]);

    const handleSubmit = useCallback(() => {
        if (previewMode) {
            onHide();
            return;
        }

        const responses: SurveyResponse[] = [];

        for (const [questionIdStr, answer] of Object.entries(answers)) {
            const question_id = parseInt(questionIdStr);
            if (Array.isArray(answer.selected)) {
                answer.selected.forEach((option_id: number) => {
                    responses.push({
                        question_id,
                        option_id,
                        ...(answer.comments?.[option_id] && {
                            comment: answer.comments[option_id],
                        }),
                    });
                });
            } else {
                responses.push({
                    question_id,
                    ...(answer.comment
                        ? { comment: answer.comment }
                        : {
                              option_id: answer.selected,
                              ...(answer.comments?.[answer.selected] && {
                                  comment: answer.comments[answer.selected],
                              }),
                          }),
                });
            }
        }

        setIsSubmitting(true);
        onSubmit?.(survey.id, responses)
            .then(() => onHide())
            .catch((err) => setError(parseApiError(err)))
            .finally(() => setIsSubmitting(false));
    }, [survey.id, questions, answers, onSubmit, onHide, setIsSubmitting]);

    const parsedDescription = survey.description
        ? htmlParser.parse(formatRichText(JSON.parse(survey.description)))
        : '';

    return (
        <>
            <StyledModal show onHide={handleDismiss} className={className}>
                {error && <StyledAlert color="error">{error?.message}</StyledAlert>}
                <StyledHeader closeButton>{survey.title}</StyledHeader>
                <StyledModalBody>
                    {parsedDescription && (
                        <SurveyDescription>
                            <BodyText2>{parsedDescription}</BodyText2>
                        </SurveyDescription>
                    )}

                    {questionsCount > 1 && (
                        <SurveyProgress>
                            {_('survey_progress_title', {
                                question: currentStep + 1,
                                total: questionsCount,
                            })}
                        </SurveyProgress>
                    )}
                    <SurveyQuestion
                        question={currentQuestion}
                        answer={answer}
                        onChange={handleAnswerChange}
                    />
                </StyledModalBody>
                <StyledFooter>
                    {isFirstStep ? (
                        <ModalButton
                            color="secondary"
                            onClick={handleShowLater}
                            disabled={!onPostpone}
                        >
                            {_('show_later_button')}
                        </ModalButton>
                    ) : (
                        <ModalButton color="secondary" onClick={handleBack}>
                            {_('back_button')}
                        </ModalButton>
                    )}

                    {isLastStep ? (
                        <ModalButton
                            type="submit"
                            onClick={handleSubmit}
                            disabled={!isCurrentStepValid || isSubmitting}
                            loading={isSubmitting}
                        >
                            {_('submit_button')}
                        </ModalButton>
                    ) : (
                        <ModalButton onClick={handleNext} disabled={!isCurrentStepValid}>
                            {_('next_button')}
                        </ModalButton>
                    )}
                </StyledFooter>
            </StyledModal>
            {previewMode && <SurveyPreviewBanner onSubmit={onPublish} onHide={onHide} />}
        </>
    );
};

export default SurveyPopup;
